/**
 * ConnectionIndicator
 *
 * Small dot indicator showing WebSocket connection state.
 * Three states: connected (green), reconnecting (yellow pulsing), disconnected (red).
 * Clicking when disconnected triggers a manual reconnect attempt.
 */

import { useWebSocketStore } from '../../store/websocketStore';
import { socketService } from '../../services/socket';

const STATUS_CONFIG = {
  connected: {
    color: 'bg-ems-green',
    label: 'Real-time connected',
    pulse: false,
    clickable: false,
  },
  reconnecting: {
    color: 'bg-ems-amber',
    label: 'Reconnecting...',
    pulse: true,
    clickable: false,
  },
  disconnected: {
    color: 'bg-ems-red',
    label: 'Disconnected - click to reconnect',
    pulse: false,
    clickable: true,
  },
} as const;

export function ConnectionIndicator() {
  const status = useWebSocketStore((s) => s.status);
  const reconnectAttempt = useWebSocketStore((s) => s.reconnectAttempt);
  const config = STATUS_CONFIG[status];

  const handleClick = () => {
    if (config.clickable) {
      socketService.reconnect();
    }
  };

  const tooltip =
    status === 'reconnecting' && reconnectAttempt > 0
      ? `Reconnecting (attempt ${reconnectAttempt})...`
      : config.label;

  return (
    <button
      onClick={handleClick}
      disabled={!config.clickable}
      className={`
        inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs
        ${config.clickable ? 'cursor-pointer hover:bg-gray-100' : 'cursor-default'}
        transition-colors
      `}
      title={tooltip}
      aria-label={tooltip}
    >
      <span
        className={`
          inline-block w-2 h-2 rounded-full ${config.color}
          ${config.pulse ? 'animate-pulse' : ''}
        `}
      />
      <span className="text-gray-500 hidden sm:inline">
        {status === 'connected' ? 'Live' : status === 'reconnecting' ? 'Reconnecting' : 'Offline'}
      </span>
    </button>
  );
}
